import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { TradingPost } from '../types/TradingTypes';
import { getTradingPostById, formatTradingDate } from '../utils/tradingUtils';
import '../assets/styles/TradingBlog.css';

const TradingPostPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<TradingPost | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const loadPost = async () => {
      if (!postId) return;
      setLoading(true);
      const postData = await getTradingPostById(postId);
      setPost(postData);
      setLoading(false);
    };
    loadPost();
  }, [postId]);

  if (loading) {
    return <div className="trading-blog-container">Loading post...</div>;
  }

  if (!post) {
    return (
      <div className="trading-blog-container">
        <div className="trading-blog-no-results">
          <h3>Trading post not found</h3>
          <p>The trading post you're looking for doesn't exist or has been removed.</p>
          <Link to="/trading" className="trading-blog-back-link">&larr; Back to all trading posts</Link>
        </div>
      </div>
    );
  }

  // Use the first screenshot as the main image
  const image = post.screenshots && post.screenshots.length > 0 ? post.screenshots[0] : '/images/blog/default.jpg';
  const fullUrl = window.location.origin + location.pathname;

  return (
    <div className="trading-blog-container">
      <Helmet>
        <title>{post.title} | Trading Blog</title>
        <meta name="description" content={post.summary} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.summary} />
        <meta property="og:image" content={image} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.summary} />
        <meta name="twitter:image" content={image} />
      </Helmet>

      <Link to="/trading" className="trading-blog-back-link">
        <span className="trading-blog-back-icon">&larr;</span> Back to all trading posts
      </Link>

      <article className="trading-post">
        <div className="trading-post-header">
          <h1 className="trading-post-title">{post.title}</h1>
          <div className="trading-post-meta">
            <span className="trading-post-date">{formatTradingDate(post.date)}</span>
            <span className="trading-post-author">By {post.author}</span>
          </div>
        </div>
        <img 
          src={image} 
          alt={post.title} 
          className="trading-post-image"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/blog/default.jpg';
          }}
        />
        
        <div className="trading-post-content">
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
        {post.trades.length > 0 && (
          <div className="trading-post-trades">
            <h3>Trades</h3>
            <table className="trades-table">
              <thead>
                <tr>
                  {post.trades[0].agent !== undefined && <th>Agent</th>}
                  <th>Symbol</th>
                  <th>Profit</th>
                  {post.trades[0].status && <th>Status</th>}
                  {post.trades[0].numTrades !== undefined && <th>Number of Trades</th>}
                  {post.trades[0].notes && <th>Notes</th>}
                </tr>
              </thead>
              <tbody>
                {post.trades.map((trade, index) => (
                  <tr key={index}>
                    {trade.agent !== undefined && <td>{trade.agent}</td>}
                    <td>{trade.symbol}</td>
                    <td className={trade.profit >= 0 ? 'profit' : 'loss'}>
                      ${trade.profit.toFixed(2)}
                    </td>
                    {trade.status && <td>{trade.status}</td>}
                    {trade.numTrades !== undefined && <td>{trade.numTrades}</td>}
                    {trade.notes && <td>{trade.notes}</td>}
                  </tr>
                ))}
                {/* Sum row */}
                <tr className="trades-table-sum-row" style={{ fontWeight: 'bold', background: '#f6f6f6' }}>
                  {/* Dynamically span columns based on which columns are present */}
                  {post.trades[0].agent !== undefined && <td colSpan={post.trades[0].notes ? 2 : 1}>Total</td>}
                  {post.trades[0].agent === undefined && <td>Total</td>}
                  {post.trades[0].status && <td></td>}
                  <td className={post.trades.reduce((sum, t) => sum + t.profit, 0) >= 0 ? 'profit' : 'loss'}>
                    ${post.trades.reduce((sum, t) => sum + t.profit, 0).toFixed(2)}
                  </td>
                  {/* Fill the rest of the columns with empty cells if needed */}
                  {post.trades[0].status && <td></td>}
                  {post.trades[0].notes && <td></td>}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {/* Embedded video if present */}
        {post.videoUrl && (
          <div className="trading-post-video" style={{ margin: '2rem 0', textAlign: 'center' }}>
            {post.videoUrl.match(/(youtube\.com|youtu\.be|vimeo\.com)/i) ? (
              <iframe
                width="560"
                height="315"
                src={post.videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                title="Trading Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ maxWidth: '100%', borderRadius: '8px' }}
              />
            ) : (
              <video
                src={post.videoUrl}
                controls
                style={{ maxWidth: '100%', borderRadius: '8px' }}
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        )}
        {post.tags.length > 0 && (
          <div className="trading-post-tags">
            {post.tags.map(tag => (
              <span key={tag} className="trading-tag">{tag}</span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

export default TradingPostPage; 